import React, { useState } from 'react';
import { AccordionDetails, Chip, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { AccountData, PassOut, PaymentEntityType, RefundablePaymentData, RefundOwnerType } from '../../../../api';
import { Accordion, AccordionSummary } from '../../../../calendar/dialogs/EditOrderDialog/components/Accordion';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { PassPaymentAuditDetails } from '../../PassActivityLog';
import { useLanguage } from '../../../../common/GeneralUtils';
import { PassDetailPanels } from '../../PassDetailTypes';
import { getPassOrderPage } from '../../../../utils/AccountUtils';
import { getRecurringSubscriptionView, isRenewalFailing, RecurringAction } from '../../utils';
import { RefundDialog } from '../../../../refunds/RefundDialog';
import { findRefundItem, useRefundOverview } from '../../../../refunds/useRefundOverview';
import { SubscriptionSection } from './SubscriptionSection';

type Props = {
  pass: PassOut;
  expandedPanel: PassDetailPanels | undefined;
  onPanelChange: (panel: PassDetailPanels) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  onUpdated: (pass: PassOut) => void;
  onRecurringAction: (action: RecurringAction) => void;
  onPauseRequest: () => void;
};

export const BillingPanel = ({
  pass,
  expandedPanel,
  onPanelChange,
  onUpdated,
  onRecurringAction,
  onPauseRequest,
}: Props) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const passOrderPage = getPassOrderPage(account, pass.order_page_id);
  const statusLabel = getRecurringSubscriptionView(pass, passOrderPage, t).label || t(`passes.status.${pass.status}`);
  // A declined renewal is otherwise invisible until the panel is opened -- this is the one signal
  // that survives the accordion being collapsed.
  const renewalFailing = isRenewalFailing(pass, passOrderPage);
  const { overview, mutate } = useRefundOverview(RefundOwnerType.PASS, pass.id);
  const [refundItem, setRefundItem] = useState<RefundablePaymentData | undefined>(undefined);

  return (
    <Accordion
      expandedPanelName={expandedPanel}
      panelName={PassDetailPanels.Billing}
      onChange={onPanelChange}
      allOpenValue={PassDetailPanels.AllOpen}>
      <AccordionSummary
        title={t('passes.swap.panel_title')}
        summaryComponent={
          <Grid container gap={0.5} alignItems="center" flexWrap="nowrap">
            {renewalFailing && (
              <Chip size="small" color="error" variant="outlined" label={t('passes.recurring.billing.failing_chip')} />
            )}
            {pass.pending_swap && (
              <Chip size="small" color="warning" variant="outlined" label={t('passes.swap.pending_chip')} />
            )}
            <Typography variant="body2" color="text.secondary" noWrap maxWidth="240px">
              {statusLabel}
            </Typography>
          </Grid>
        }
      />
      <AccordionDetails>
        <Grid container flexDirection="column" gap={1}>
          <SubscriptionSection
            pass={pass}
            onUpdated={onUpdated}
            onRecurringAction={onRecurringAction}
            onPauseRequest={onPauseRequest}
          />

          <Typography fontWeight={700}>{t('passes.recurring.billing.title')}</Typography>
          {!pass.payments?.length ? (
            <Typography variant="body2" color="text.secondary">
              {t('passes.recurring.billing.empty')}
            </Typography>
          ) : (
            <Grid container flexDirection="column" gap={1}>
              {[...pass.payments]
                .sort((a, b) => moment(b.cycle_start || b.paid_at).diff(moment(a.cycle_start || a.paid_at)))
                .map((payment, i) => (
                  <PassPaymentAuditDetails
                    key={`${payment.provider_transaction_id}-${i}`}
                    payment={payment}
                    account={account}
                    refundItem={findRefundItem(
                      overview,
                      PaymentEntityType.PASS_PAYMENT,
                      payment.provider_transaction_id ?? '',
                    )}
                    onRefund={setRefundItem}
                  />
                ))}
            </Grid>
          )}
        </Grid>
        <RefundDialog
          isOpen={!!refundItem}
          onClose={() => setRefundItem(undefined)}
          item={refundItem}
          ownerLabel={passOrderPage?.label[language] ?? t('refunds.entity.pass')}
          onRefunded={() => {
            void mutate();
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
};
